import { ReactNode, useEffect, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'

/** Use this to redirect without rendering <Navigate> (avoids Router context issues when guard is from shared). */
export type NavigateFn = (to: string, options?: { replace?: boolean; state?: unknown }) => void

function AuthLoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50" aria-label="Loading">
      <div className="flex flex-col items-center gap-3 text-slate-600">
        <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
        <span>Loading…</span>
      </div>
    </div>
  )
}

/** Location-like shape for redirect state. */
export type LocationLike = { pathname: string; search?: string; hash?: string; state?: unknown; key?: string }

type RequireAuthInnerProps = {
  children: ReactNode
  requireCompany?: boolean
  requireAdmin?: boolean
  requireCustomer?: boolean
  loginPath?: string
  fallbackPath?: string
  location: LocationLike
  /** When set, redirects are done via this fn (useEffect) instead of <Navigate>. Use from app so Router context is correct. */
  navigate?: NavigateFn
}

/**
 * Inner guard that uses a provided location (no Router hook). Use this when the route
 * element is rendered in a context where useLocation() can throw (e.g. navigation timing).
 * When `navigate` is passed, redirects use it (useEffect) instead of <Navigate> to avoid
 * Router context issues when the guard runs with a different react-router instance.
 */
export function RequireAuthInner({
  children,
  requireCompany,
  requireAdmin,
  requireCustomer,
  loginPath = '/',
  fallbackPath = '/',
  location,
  navigate: navigateFn,
}: RequireAuthInnerProps) {
  const hasHydrated = useAuthStore((s) => s._hasHydrated)
  const customer = useAuthStore((s) => s.customer)
  const me = useAuthStore((s) => s.me)
  const didRedirect = useRef(false)

  const authenticated = !!customer?.token && !!customer?.user

  // Compute redirect target once (no hooks in branches).
  let redirectTo: string | null = null
  let redirectState: unknown = undefined
  if (requireCompany) {
    if (!authenticated) {
      redirectTo = loginPath
      redirectState = { from: location }
    } else if (me == null || !me.has_company) redirectTo = fallbackPath
  } else if (requireAdmin) {
    if (!authenticated) {
      redirectTo = loginPath
      redirectState = { from: location }
    } else if (me == null || !me.is_admin) redirectTo = fallbackPath
  } else if (requireCustomer) {
    if (!authenticated) {
      redirectTo = loginPath
      redirectState = { from: location }
    }
  } else if (!authenticated) {
    redirectTo = loginPath
    redirectState = { from: location }
  }

  useEffect(() => {
    if (!redirectTo || !navigateFn || didRedirect.current) return
    didRedirect.current = true
    navigateFn(redirectTo, { replace: true, state: redirectState })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- redirectState is from closure; we only want to run when redirectTo/navigateFn change
  }, [redirectTo, navigateFn])

  if (!hasHydrated) return <AuthLoadingScreen />
  if (redirectTo) {
    if (navigateFn) return <AuthLoadingScreen />
    return <Navigate to={redirectTo} state={redirectState} replace />
  }
  return <>{children}</>
}

type Props = RequireAuthInnerProps & { location?: LocationLike }

/**
 * Protects routes: one token; has_company and is_admin come from /me.
 * - requireCompany: customer token + me.has_company
 * - requireAdmin: customer token + me.is_admin
 * - requireCustomer: any authenticated user (has token)
 * Use RequireAuthInner with a passed location when the route element may run outside Router context.
 */
export function RequireAuth(props: Props) {
  const location = useLocation()
  return <RequireAuthInner {...props} location={props.location ?? location} />
}
